import React, { forwardRef, useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  IconButton,
  ListItemText,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { PinnacleTextField } from '../../../components/PinnacleTextField';
import { Program } from '../../../api/interfaces/Program';
import { Subject } from '../../../api/interfaces/Subject';
import { getSubjects } from '../../../api/services/subjects';
import { handleApiError } from '../../../utils/errorHandler';
import { pushNotification } from '../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider';
import { NotificationTypes } from '../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types';
import { NO_DATA_TO_DISPLAY_URL } from '../../../utils/constants';

interface AssignSubjectsBulkUsersDrawerContentProps {
  isSubmitting: boolean;
  selectedFile: File | null;
  onFileChange: (file: File | null) => void;
  onSubmit: (file: File) => void;
  programs: Program[];
  programId: string;
  setProgramId: React.Dispatch<React.SetStateAction<string>>;
  subjectIds: string[];
  setSubjectIds: React.Dispatch<React.SetStateAction<string[]>>;
}

const AssignSubjectsBulkUsersDrawerContent = forwardRef<HTMLInputElement, AssignSubjectsBulkUsersDrawerContentProps>(
  ({ isSubmitting, selectedFile, onFileChange, onSubmit, programs, programId, setProgramId, subjectIds, setSubjectIds }, fileInputRef) => {
    const [subjects, setSubjects] = useState<Subject[]>([]);
    const [selectedSubjects, setSelectedSubjects] = useState<Subject[]>([]);
    const [loadingSubjects, setLoadingSubjects] = useState<boolean>(false);

    const hasProgram = !!programId;

    const fetchSubjects = useCallback(async () => {
      if (!programId) {
        setSubjects([]);
        return;
      }
      try {
        setLoadingSubjects(true);
        const subjectsResponse = await getSubjects({ programId });
        setSubjects(subjectsResponse.data.data);
      } catch (error) {
        handleApiError(error, {
          notify: (msg) => {
            pushNotification({ details: msg, type: NotificationTypes.Error });
          },
        });
      } finally {
        setLoadingSubjects(false);
      }
    }, [programId]);

    useEffect(() => {
      fetchSubjects();
    }, [fetchSubjects]);

    const handleProgramChange = (nextProgramId: string) => {
      setProgramId(nextProgramId);
      // Reset the subject selection whenever the program changes.
      setSelectedSubjects([]);
      setSubjectIds([]);
    };

    const isSubjectSelected = (subjectId: string) =>
      selectedSubjects.some((s) => s._id === subjectId);

    const handleAddSubject = (subject: Subject) => {
      if (selectedSubjects.some((s) => s._id === subject._id)) return;
      const next = [...selectedSubjects, subject];
      setSelectedSubjects(next);
      setSubjectIds(next.map((s) => s._id));
    };

    const handleRemoveSubject = (subjectId: string) => {
      const next = selectedSubjects.filter((s) => s._id !== subjectId);
      setSelectedSubjects(next);
      setSubjectIds(next.map((s) => s._id));
    };

    const handleUploadClick = () => {
      (fileInputRef as React.RefObject<HTMLInputElement>).current?.click();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0] || null;
      onFileChange(file);
    };

    const handleSubmit = () => {
      if (selectedFile) {
        onSubmit(selectedFile);
      }
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ pb: 2 }}>
          <Alert severity="info" icon={false} sx={{ backgroundColor: '#E5F8FE', color: '#26C6F9' }}>
            <Typography variant="subtitle2">Bulk upload allows you to assign one or more subjects to multiple users in one action.</Typography>
          </Alert>
        </Box>

        {/* Program dropdown */}
        <PinnacleTextField
          select
          label="Program"
          name="programId"
          value={programId}
          onChange={(e) => handleProgramChange(e.target.value)}
          fullWidth
          helperText=" "
          disabled={isSubmitting}
        >
          {programs.map((program: Program) => (
            <MenuItem key={program._id} value={program._id}>
              <Typography
                noWrap
                sx={{
                  maxWidth: '400px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'block',
                }}
              >
                {program.name}
              </Typography>
            </MenuItem>
          ))}
        </PinnacleTextField>

        {/* Subjects multi-select (supplied per selected program) */}
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
            <Typography variant="body1" sx={{ color: '#333533', fontWeight: '600' }}>
              Add Subjects
              {!hasProgram && (
                <Tooltip title="Please select a program in order to add or remove a subject." arrow>
                  <IconButton sx={{ p: 0.5 }}>
                    <InfoOutlinedIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Typography>
          </Box>

          <Box display="flex" flexDirection="column" sx={{ mt: 1 }}>
            <Autocomplete
              fullWidth
              options={subjects}
              getOptionLabel={(option: Subject) => option.name}
              clearOnBlur
              disableClearable
              autoHighlight
              selectOnFocus={false}
              value={null as any}
              onChange={() => { }}
              disabled={isSubmitting || !hasProgram || loadingSubjects}
              loading={loadingSubjects}
              renderInput={(params) => (
                <PinnacleTextField
                  {...params}
                  placeholder="Search / Enter here"
                  variant="outlined"
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingSubjects ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
              renderOption={(props, option) => {
                const { key, ...rest } = props;
                return (
                  <Box
                    component="li"
                    key={key}
                    {...rest}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%',
                      px: 1,
                    }}
                  >
                    <ListItemText primary={option.name} />
                    {isSubjectSelected(option._id) ? (
                      <Typography variant="body2" color="text.secondary">
                        added
                      </Typography>
                    ) : (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddSubject(option);
                        }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                );
              }}
            />

            <Typography variant="body1" sx={{ color: '#333533', fontWeight: '600', pt: 4, pb: 4 }}>
              List of Assigned Subjects
            </Typography>

            {selectedSubjects.length > 0 ? (
              <Stack spacing={1}>
                {selectedSubjects.map((subject) => (
                  <Box
                    key={subject._id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      px: 2,
                      py: 1,
                    }}
                  >
                    <Typography>{subject.name}</Typography>
                    <IconButton size="small" onClick={() => handleRemoveSubject(subject._id)}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  color: '#818181',
                }}
              >
                <Box component="img" src={NO_DATA_TO_DISPLAY_URL} alt="No data" sx={{ width: '300px', mb: 1 }} />
              </Box>
            )}
          </Box>
        </Box>

        {/* Upload Box */}
        <Stack spacing={2}>
          <Box
            onClick={handleUploadClick}
            sx={{
              border: '2px dashed #ccc',
              borderRadius: '8px',
              p: 2,
              cursor: 'pointer',
              '&:hover': { backgroundColor: '#f9f9f9' },
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              {selectedFile ? (
                <>
                  <CloudUploadIcon sx={{ fontSize: 40, color: '#A8A8A8', height: '50px', width: '50px' }} />
                  <Box>
                    <Typography
                      variant="subtitle1"
                      fontWeight={500}
                      noWrap
                      sx={{
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        maxWidth: 200
                      }}
                    >
                      {selectedFile.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Click to change file
                    </Typography>
                  </Box>
                </>
              ) : (
                <>
                  <CloudUploadIcon sx={{ fontSize: 40, color: '#A8A8A8', height: '50px', width: '50px' }} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight={500}>
                      Upload XLSX File
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Browse file to upload
                    </Typography>
                  </Box>
                </>
              )}
            </Stack>
            <input
              type="file"
              accept=".xlsx"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          </Box>
        </Stack>

        {/* Submit Button */}
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={isSubmitting || !selectedFile || !programId || subjectIds.length === 0}
          startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
        >
          ADD SUBJECTS
        </Button>
      </Box>
    );
  }
);

export default AssignSubjectsBulkUsersDrawerContent;
